/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform, useWindowDimensions } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  DEFAULT_CURRENCY,
  QUERY_PERSIST_CACHE,
  useAppConfig,
  useLocale,
} from '@airasia-rn/ui';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { useCurrency } from '@airasia-rn/ui/config';
import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query';
import {
  useUser,
  useIsLoggedIn,
  useUserId,
  useAccessToken,
  useRefreshToken,
} from '@airasia-rn/account';

import type {
  VouchersResponse,
  SmartFillResponse,
  UseSmartFillSuggestionsParams,
  PropertyNameSmartFillRequest,
  SearchHotelItem,
  HotelSearchResultData,
  HotelListParams,
  HotelBookingConfirmationResponse,
  HotelBookingPrecancellationResponse,
  HotelCancelBookingRequest,
  HotelCancelBookingResponse,
  HotelEmailVoucherRequest,
  HotelEmailVoucherResponse,
  HotelBookingRoomContentResponse,
  HotelDetailResponse,
  Suggestions,
  PopularSearchesResponse,
  HotelData,
} from '../types';
import { useHotelConfig } from '../configs/HotelConfigProvider';
import { createJwtTokenProvider } from '../services/auth';
import {
  HotelsApiService,
  HotelsRecommendationService,
  HotelsSearchService,
} from '../services';
import { StaticApiService } from '../services/StaticApiService';
import {
  FEATURE_DESTINATIONS,
  HOTELS_DOMAIN,
  TARGETING_CODES,
  LOB,
  DEFAULT_COUNTRY,
  DEFAULT_NEAREST_AIRPORT,
  HOTEL_LAST_SEARCH_PARAMS_STORAGE_KEY,
  HOTEL_BOOKING_TOKEN_ENDPOINT,
  HOTEL_CHECKOUT_TOKEN_ENDPOINT,
} from '../configs/constants';
import { useLogger } from '@airasia-rn/core-logger';
import { getRecentSearchContentCards } from '../widgets/RecentSearchView/utils';
import { useRecentSearches as useRecentSearchesStorage } from './useRecentSearch';
import {
  sanitizeAndMapHotelsResponse,
  transformApiToHotelData,
} from '../widgets/HotelListView/utils';
import { SearchWidgetEvent, SearchWidgetMeta } from '../analytics';
import { useHotelSearchParams } from './useHotelSearchParams';
import { normalizeBookingConfirmationKey } from './confirmation';
import { resolveHotelSearchCurrency } from '../utils/currency';
import { useResolvedNearestAirports } from '../configs/HotelIntegrationsProvider';

export const hotelsKeys = {
  search: [QUERY_PERSIST_CACHE, 'hotels', 'search'],
  booking: [QUERY_PERSIST_CACHE, 'hotels', 'booking'],
  bookingConfirmation: (key: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'confirmation',
    key,
  ],
  bookingPrecancellation: (preCancelHref: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'precancellation',
    preCancelHref,
  ],
  bookingEmailVoucher: (voucherHref: string, email: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'emailVoucher',
    voucherHref,
    email,
  ],
  bookingRoomContent: (hotelId: string, roomId: string, language: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'roomContent',
    hotelId,
    roomId,
    language,
  ],
  bookingHotelContent: (hotelId: string, language: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'hotelContent',
    hotelId,
    language,
  ],
  hotelToken: [QUERY_PERSIST_CACHE, 'hotels', 'token'],
  vouchers: (email: string, currencyCode: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'vouchers',
    email,
    currencyCode,
  ],
  smartFillSuggestions: (countryCode: string, trimQuery: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    countryCode,
    trimQuery,
  ],
  featureDestinations: [
    QUERY_PERSIST_CACHE,
    HOTELS_DOMAIN,
    FEATURE_DESTINATIONS,
  ],
  banner: (countryCode: string) => [
    QUERY_PERSIST_CACHE,
    'hotels',
    'banner',
    countryCode,
  ],
  specialRequest: [QUERY_PERSIST_CACHE, HOTELS_DOMAIN, 'specialRequest'],
  hotelDeals: [QUERY_PERSIST_CACHE, 'hotels', 'hotelDeals'],
  hotelsList: [HOTELS_DOMAIN, 'hotelsList'],
  smartFill: [QUERY_PERSIST_CACHE, HOTELS_DOMAIN, 'smartfill'],
  propertyNameSmartFillSuggestions: (
    trimQuery: string,
    lat: number,
    lon: number,
  ) => [
    QUERY_PERSIST_CACHE,
    HOTELS_DOMAIN,
    'propertyNameSmartfill',
    trimQuery,
    lat,
    lon,
  ],
  detail: {
    hotelDetail: (hotelId: string, language: string, env: string) =>
      [
        QUERY_PERSIST_CACHE,
        'hotels',
        'details',
        hotelId,
        language,
        env,
      ] as const,
  },
  managedGuestList: [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'managedGuestList',
  ],
  pseudoFavMembers: [
    QUERY_PERSIST_CACHE,
    'hotels',
    'booking',
    'pseudoFavMembers',
  ],
};

export const getHotelsListQueryKey = (
  params: any,
  appConfig: any,
  user: any,
  currency: string = appConfig.currency,
) => {
  const normalize = (val: any) => {
    if (Array.isArray(val))
      return val.length > 0 ? [...val].sort().join(',') : undefined;
    if (val === null || val === '') return undefined;
    return val;
  };

  return [
    ...hotelsKeys.hotelsList,
    appConfig.env,
    resolveHotelSearchCurrency([currency, params?.currency], DEFAULT_CURRENCY),
    params.location_id,
    params.checkin,
    params.checkout,
    params.occupancy,
    params.user_selected_hotel_id ?? null,
    params.guest_score ?? null,
    normalize(params.star_rating),
    normalize(params.amenity),
    normalize(params.rate_amenity),
    normalize(params.category),
    normalize(params.payment_method),
    params.debug,
    params.channel,
    params.asProfile ?? null,
    params.language?.toLowerCase(),
    user?.loyaltyId,
    user?.loyaltyTier,
  ];
};

/** `currency` sits directly after the hotelsList prefix + env in the list query key. */
export const HOTELS_LIST_CURRENCY_QUERY_KEY_INDEX =
  hotelsKeys.hotelsList.length + 1;

/** `location_id` sits after hotelsList prefix + env + currency in the list query key. */
export const HOTELS_LIST_LOCATION_ID_QUERY_KEY_INDEX =
  hotelsKeys.hotelsList.length + 2;

/**
 * Keep previous hotel-list pages only for same-destination and same-currency refetches (filters,
 * dates, etc.). Destination or currency changes must drop placeholder data so old hotels
 * and stale prices do not flash while the new search loads.
 */
export const shouldKeepHotelSearchPlaceholderData = (
  previousQuery: { queryKey: readonly unknown[] } | undefined,
  nextLocationId: unknown,
  nextCurrency?: unknown,
): boolean => {
  if (!previousQuery || nextLocationId == null || nextLocationId === '') {
    return false;
  }

  const previousLocationId =
    previousQuery.queryKey[HOTELS_LIST_LOCATION_ID_QUERY_KEY_INDEX];

  if (previousLocationId !== nextLocationId) {
    return false;
  }

  if (nextCurrency != null && nextCurrency !== '') {
    const previousCurrency =
      previousQuery.queryKey[HOTELS_LIST_CURRENCY_QUERY_KEY_INDEX];
    if (previousCurrency && previousCurrency !== nextCurrency) {
      return false;
    }
  }

  return true;
};

/** Returns a memoized HotelsApiService configured for the current environment. */
export function useHotelsApiService(): HotelsApiService {
  const { config: appConfig } = useAppConfig();
  return useMemo(() => {
    return new HotelsApiService(appConfig.env);
  }, [appConfig.env]);
}
/** Returns a memoized StaticAPiServices configured for the current environment. */
export function useStaticBucketApiService(): StaticApiService {
  const { config: appConfig } = useAppConfig();

  return useMemo(() => {
    return new StaticApiService({
      environment: appConfig.env,
    });
  }, [appConfig.env]);
}

export const usePopularSearches = (enabled: boolean) => {
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const { geoId: countryCodeFromContext } = useSuperAppContext();
  const activeGeoId =
    hotelConfig?.geoId ?? appConfig.geoId ?? countryCodeFromContext ?? 'MY';
  const api = useStaticBucketApiService();
  const { currentLanguage } = useLocale();

  return useQuery({
    queryKey: ['popularSearches', activeGeoId, currentLanguage, appConfig.env],
    queryFn: async (): Promise<Suggestions[]> => {
      try {
        const response = await api.fetchPopularSearches();
        const country = (activeGeoId || 'default').toUpperCase();

        // Lookup country array, fallback to 'default'
        const countryData = response[country] || response['default'];
        if (!countryData || !Array.isArray(countryData.suggestions)) {
          return [];
        }

        const locale = (currentLanguage || 'en-gb').toLowerCase();

        // Pre-calculate ranks for quick O(1) lookup inside the sort callback
        const rankMap = new Map(
          countryData.suggestions.map((s) => [
            String(s.location_id),
            s.rank ?? 999,
          ]),
        );

        // Map and sort raw suggestions
        const mappedSuggestions: Suggestions[] = countryData.suggestions
          .map((item) => {
            // Retrieve localized name, fallback to 'en-gb'
            const localizedName = item.name[locale] || item.name['en-gb'] || '';

            return {
              id: String(item.location_id),
              aa_id: item.aaid || '',
              name: localizedName,
              full_name: localizedName,
              country_code:
                country !== 'DEFAULT' && country !== 'default' ? country : 'MY',
              coordinates: { lat: 0, lon: 0 },
              city_id: null,
              type: item.aaid ? 'hotel' : 'city',
            } satisfies Suggestions;
          })
          .sort((a, b) => {
            const rankA = rankMap.get(a.id) ?? 999;
            const rankB = rankMap.get(b.id) ?? 999;
            return rankA - rankB;
          });

        return mappedSuggestions;
      } catch (error) {
        console.error('Failed to fetch/parse popular searches:', error);
        return [];
      }
    },
    enabled: enabled && !!appConfig.env,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
};

export function useHotelDealsApiService(): HotelsRecommendationService {
  const { config } = useAppConfig();

  return useMemo(() => {
    return new HotelsRecommendationService(config.env);
  }, [config.env]);
}
export function useHotelSearchApiService(): HotelsSearchService {
  const { config } = useAppConfig();

  return useMemo(() => {
    return new HotelsSearchService(config.env);
  }, [config.env]);
}
/**
 * Hook to fetch hotel authentication token
 * Uses JWT token provider to get authentication token for hotel services
 *
 * @returns React Query result with hotel token
 *
 * @example
 * ```tsx
 * const { data: hotelToken, isLoading } = useHotelToken();
 * ```
 */
export const useHotelToken = () => {
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();

  const tokenProvider = useMemo(
    () =>
      createJwtTokenProvider(
        appConfig.env,
        hotelConfig?.jwtHash || '',
        hotelConfig?.xorKey || '',
      ),
    [appConfig.env, hotelConfig?.jwtHash, hotelConfig?.xorKey],
  );

  const query = useQuery({
    queryKey: [
      ...hotelsKeys.hotelToken,
      hotelConfig?.jwtHash,
      hotelConfig?.xorKey,
      appConfig.env,
    ],
    queryFn: () => tokenProvider.getToken(),
    staleTime: 0,
    enabled: !!hotelConfig?.jwtHash && !!hotelConfig?.xorKey && !!appConfig.env,
  });

  const refetchToken = useCallback(
    (forceRefresh?: boolean) => tokenProvider.getToken(undefined, forceRefresh),
    [tokenProvider],
  );
  const refetchTokenByEndpoint = useCallback(
    (endpoint: string, forceRefresh?: boolean) =>
      tokenProvider.getToken(endpoint, forceRefresh),
    [tokenProvider],
  );

  const { data, ...rest } = query;

  return {
    ...rest,
    data,
    refetchToken,
    refetchTokenByEndpoint,
  };
};

/**
 * Hook to fetch hotel vouchers/promos
 * Automatically handles authentication with access token and client ID
 *
 * @returns React Query result with vouchers data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useVouchers();
 * ```
 */
export const useVouchers = () => {
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const hotelTokenQuery = useHotelToken();
  const userQuery = useUser('hotels');
  const { isLoggedIn } = useIsLoggedIn('hotels');

  const currencyCode = currency;
  const username = userQuery.data?.username || '';

  return useQuery({
    queryKey: [...hotelsKeys.vouchers(username, currencyCode), appConfig.env],
    queryFn: async (): Promise<VouchersResponse> => {
      const token = await hotelTokenQuery.refetchToken();
      return api.getVouchers(
        {
          email: username,
          currencyCode,
        },
        token || '',
        hotelConfig?.clientId || '',
      );
    },
    enabled:
      isLoggedIn &&
      !!username &&
      !!hotelConfig?.clientId &&
      !!hotelTokenQuery?.data &&
      !!appConfig.env,
  });
};

export const useHotelSearch = (params: any, locationName?: string) => {
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { currentLanguage, isLoading: isLocaleLoading } = useLocale();
  const { t } = useTranslation('hotels');
  const { config: hotelConfig } = useHotelConfig();
  const hotelTokenQuery = useHotelToken();
  const api = useHotelSearchApiService();
  const accessTokenQuery = useAccessToken('hotels');
  const refreshTokenQuery = useRefreshToken('hotels');
  const userQuery = useUser('hotels');
  const accessToken = accessTokenQuery.data;
  const refreshToken = refreshTokenQuery.data;
  const user = userQuery.data;

  const query = useInfiniteQuery({
    queryKey: getHotelsListQueryKey(params, appConfig, user, currency),

    queryFn: async ({ pageParam = 0, signal }) => {
      const token = await hotelTokenQuery.refetchToken();
      const searchParams = {
        ...params,
        currency: resolveHotelSearchCurrency(
          [currency, params?.currency],
          DEFAULT_CURRENCY,
        ),
        page_number: pageParam,
        ...(user?.loyaltyId && { big_member_id: user.loyaltyId }),
        ...(user?.loyaltyTier && { mType: user.loyaltyTier }),
      };
      const rawResponse = await api.searchHotels(
        searchParams,
        token || '',
        hotelConfig?.clientId || '',
        accessToken || undefined,
        refreshToken || undefined,
        signal,
      );

      const sanitized = sanitizeAndMapHotelsResponse({
        status: 200,
        body: rawResponse,
      });

      return sanitized.body;
    },

    select: (data): HotelSearchResultData & { flyerDiscount?: boolean } => {
      const hotels = data.pages.flatMap((page) => {
        const hotelsArray = page?.hotels ?? [];
        return hotelsArray.map((hotel: SearchHotelItem) =>
          transformApiToHotelData(
            hotel,
            locationName || '',
            page?.stay_detail,
            t,
          ),
        );
      });

      const firstPageAggregations =
        data.pages[0]?.aggregations?.aggregationList?.aggregations || [];
      const popularFilters = data.pages[0]?.filters?.popular_filters ?? [];

      const flyerDiscount = (data.pages[0] as any)?.flyer_discount;

      return {
        hotels,
        aggregations: firstPageAggregations,
        popularFilters,
        hasNextPage:
          data.pages[data.pages.length - 1]?.has_more_hotels ?? false,
        flyerDiscount,
      };
    },

    initialPageParam: 0,

    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.hotels?.length === 0) {
        return undefined;
      }
      return lastPage?.has_more_hotels ? allPages.length : undefined;
    },
    enabled:
      !!params.location_id &&
      !!hotelTokenQuery.data &&
      !!hotelConfig?.clientId &&
      !isLocaleLoading &&
      (!currentLanguage ||
        !params.language ||
        currentLanguage.toLowerCase() === params.language.toLowerCase()),
    placeholderData: (previousData, previousQuery) =>
      shouldKeepHotelSearchPlaceholderData(
        previousQuery,
        params.location_id,
        resolveHotelSearchCurrency(
          [currency, params?.currency],
          DEFAULT_CURRENCY,
        ),
      )
        ? previousData
        : undefined,
    staleTime: 30 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const isBootstrapLoading =
    !!params.location_id &&
    !query.data &&
    !query.isError &&
    (isLocaleLoading ||
      hotelTokenQuery.isLoading ||
      hotelTokenQuery.isFetching);

  // Automatically fetch next page if server-filtered results are less than 3.
  const totalHotels = query.data?.hotels.length || 0;
  const hasServerSideFilter = Boolean(
    params.landmark ||
      params.guest_score ||
      params.user_selected_hotel_id ||
      (Array.isArray(params.star_rating)
        ? params.star_rating.length > 0
        : params.star_rating) ||
      (params.amenity?.length ?? 0) > 0 ||
      (params.rate_amenity?.length ?? 0) > 0 ||
      (params.category?.length ?? 0) > 0 ||
      (params.payment_method?.length ?? 0) > 0,
  );
  useEffect(() => {
    if (
      hasServerSideFilter &&
      totalHotels > 0 &&
      totalHotels < 3 &&
      query.hasNextPage &&
      !query.isFetchingNextPage
    ) {
      query.fetchNextPage();
    }
  }, [
    hasServerSideFilter,
    totalHotels,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
  ]);

  useEffect(() => {
    if (params?.location_id) {
      AsyncStorage.setItem(
        HOTEL_LAST_SEARCH_PARAMS_STORAGE_KEY,
        JSON.stringify(params),
      );
    }
  }, [params]);

  return {
    ...query,
    isLoading: query.isLoading || isBootstrapLoading,
  };
};

export const usePropertyNameSmartFillSuggestions = ({
  query,
  lat,
  lon,
}: {
  query: string;
  lat?: number | null;
  lon?: number | null;
}) => {
  const { config } = useAppConfig();
  const api = useHotelSearchApiService();
  const { config: hotelConfig } = useHotelConfig();
  const hotelTokenQuery = useHotelToken();
  const trimmedQuery = query.trim();
  const hasCoordinates =
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    typeof lon === 'number' &&
    Number.isFinite(lon);

  return useQuery({
    queryKey: [
      ...hotelsKeys.propertyNameSmartFillSuggestions(
        trimmedQuery,
        lat ?? 0,
        lon ?? 0,
      ),
      config.env,
    ],
    queryFn: async (): Promise<SmartFillResponse> => {
      const request: PropertyNameSmartFillRequest = {
        language: config.locale || 'en-gb',
        query: trimmedQuery,
        lat: lat ?? 0,
        lon: lon ?? 0,
      };
      const token = await hotelTokenQuery.refetchToken();

      return api.fetchPropertyNameSuggestions(
        request,
        token || '',
        hotelConfig?.clientId || '',
      );
    },
    enabled:
      trimmedQuery.length >= 3 &&
      hasCoordinates &&
      !!hotelTokenQuery?.data &&
      !!hotelConfig?.clientId &&
      !!config.env,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};
export const useHotelRecommendations = (lob: string = LOB) => {
  const { config } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const { currency } = useCurrency();
  const { currentLanguage } = useLocale();
  const api = useHotelDealsApiService();
  const { data: userId } = useUserId();
  const [settledLocale, setSettledLocale] = useState<string | null>(null);

  const { data: nearestAirportSuccess } = useResolvedNearestAirports();
  const normalizedCurrentLanguage = currentLanguage?.toLowerCase();

  useEffect(() => {
    if (!normalizedCurrentLanguage) {
      return;
    }

    // Locale can briefly flip between old and new values during selection.
    // Wait for it to settle before triggering the recommendations query.
    const timeoutId = setTimeout(() => {
      setSettledLocale(normalizedCurrentLanguage);
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [normalizedCurrentLanguage]);

  const payload = {
    userId: userId,
    locale: settledLocale || 'en-gb',
    lob: [lob],
    location: {
      country: nearestAirportSuccess?.country || DEFAULT_COUNTRY,
      nearestAirport:
        nearestAirportSuccess?.airport?.[0] ||
        hotelConfig.nearestAirport ||
        DEFAULT_NEAREST_AIRPORT,
    },
    currency: resolveHotelSearchCurrency([currency], DEFAULT_CURRENCY),
    cardCount: {
      hotel: 10,
    },
    debug: true,
    targetingCodes: {
      hotel: TARGETING_CODES,
    },
  };

  return useQuery({
    queryKey: [
      ...hotelsKeys.hotelDeals,
      payload.userId,
      payload.locale,
      payload.currency,
      payload.location.country,
      payload.location.nearestAirport,
      config.env,
    ],
    enabled: Boolean(
      userId && payload.location.nearestAirport && settledLocale,
    ),
    queryFn: async () => api.getHotelDeals(payload),
  });
};

export const useSmartFillApi = (locationId: string) => {
  const { config } = useAppConfig();
  const api = useHotelSearchApiService();
  const { config: hotelConfig } = useHotelConfig();
  const hotelTokenQuery = useHotelToken();

  return useQuery({
    // Add locationId and locale to the key so the cache is unique per location and language
    queryKey: [
      ...hotelsKeys.featureDestinations,
      config.env,
      locationId,
      config.locale,
    ],
    queryFn: async () => {
      if (!locationId || !hotelConfig?.clientId) return null;
      const token = await hotelTokenQuery.refetchToken();
      const response = await api.fetchSmartfill(
        locationId,
        token || '',
        hotelConfig.clientId,
        config.locale || 'en-gb',
      );
      return response;
    },
    enabled: !!locationId && !!hotelConfig?.clientId,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};

export const useSmartFillSuggestions = ({
  query,
}: UseSmartFillSuggestionsParams) => {
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const { data: nearestAirportData } = useResolvedNearestAirports();
  const api = useHotelsApiService();
  const hotelTokenQuery = useHotelToken();
  const { logEvent } = useLogger();
  const lastLoggedResultRef = useRef('');

  const countryCode = (
    nearestAirportData?.country || DEFAULT_COUNTRY
  ).toUpperCase();
  const { currentLanguage } = useLocale();

  const smartFillQuery = useQuery({
    queryKey: [
      ...hotelsKeys.smartFillSuggestions(countryCode, query),
      appConfig.env,
    ],
    queryFn: async (): Promise<SmartFillResponse> => {
      const token = await hotelTokenQuery.refetchToken();
      return api.getSmartFillSuggestions(
        {
          countryCode,
          query,
          language: currentLanguage,
        },
        token || '',
        hotelConfig?.clientId || '',
      );
    },
    enabled:
      query?.length >= 3 &&
      !!hotelTokenQuery?.data &&
      !!hotelConfig?.clientId &&
      !!appConfig.env,
  });

  useEffect(() => {
    const safeQuery = query?.trim();
    const suggestions = smartFillQuery.data?.suggestions ?? [];

    if (!safeQuery) {
      lastLoggedResultRef.current = '';
      return;
    }

    if (suggestions.length === 0) {
      return;
    }

    const signature = `${safeQuery}::${suggestions
      .map((suggestion) => `${suggestion.id}-${suggestion.type}`)
      .join('|')}`;

    if (lastLoggedResultRef.current === signature) {
      return;
    }

    lastLoggedResultRef.current = signature;
    const params = SearchWidgetEvent.smartFillResultsEvent(suggestions);
    logEvent(SearchWidgetMeta.EVENT_NAMES.SMART_FILL_RESULTS, params);
  }, [query, smartFillQuery.data, logEvent]);

  return smartFillQuery;
};

export const useFeaturedDestinations = () => {
  const { config } = useAppConfig();
  const api = useStaticBucketApiService();
  return useQuery({
    queryKey: [...hotelsKeys.featureDestinations, config.env],
    queryFn: () => api.getFeaturedDestinations(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Hook to fetch all banner data
 */
export const useHotelBanners = (countryCode?: string) => {
  const api = useStaticBucketApiService();
  const { config: appConfig } = useAppConfig();
  const normalizedCountryCode = countryCode?.trim() || 'default';
  return useQuery({
    queryKey: [...hotelsKeys.banner(normalizedCountryCode), appConfig.env],
    queryFn: () => api.fetchBanners(),
    staleTime: 15 * 60 * 1000, // 15mins
    gcTime: 30 * 60 * 1000, // 30mins
  });
};

/**
 * Hook to fetch hotel special request options
 */
export const useHotelSpecialRequest = () => {
  const api = useStaticBucketApiService();
  const { config: appConfig } = useAppConfig();

  return useQuery({
    queryKey: [...hotelsKeys.specialRequest, appConfig.env],
    queryFn: () => api.fetchSpecialRequest(),
    staleTime: 15 * 60 * 1000, // 15mins
    gcTime: 30 * 60 * 1000, // 30mins
  });
};

export const normalizeBookingContentLanguage = (language?: string) => {
  const normalizedLanguage = language?.trim().toLowerCase() || 'en-us';

  return normalizedLanguage === 'en-gb' ? 'en-us' : normalizedLanguage;
};

const getHotelServiceAccountClientId = (configClientId?: string) =>
  configClientId?.trim() ||
  process.env.EXPO_PUBLIC_HOTELS_SA_CLIENT_ID?.trim() ||
  (
    globalThis as typeof globalThis & {
      process?: { env?: Record<string, string | undefined> };
    }
  ).process?.env?.EXPO_PUBLIC_HOTELS_SA_CLIENT_ID?.trim() ||
  '';

const isBrowserHardReload = () => {
  const performanceApi = globalThis.performance as
    | {
        getEntriesByType?: (type: string) => { type?: string }[];
        navigation?: { type?: number };
      }
    | undefined;
  const navigationEntry = performanceApi?.getEntriesByType?.('navigation')?.[0];

  return (
    navigationEntry?.type === 'reload' || performanceApi?.navigation?.type === 1
  );
};

export const useHotelBookingConfirmation = (key?: string) => {
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const queryClient = useQueryClient();
  const hotelTokenQuery = useHotelToken();
  const normalizedKey = normalizeBookingConfirmationKey(key);
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);
  const confirmationCurrency = resolveHotelSearchCurrency(
    [currency, appConfig.currency],
    DEFAULT_CURRENCY,
  );
  const queryKey = useMemo(
    () => [
      ...hotelsKeys.bookingConfirmation(normalizedKey),
      appConfig.env,
      confirmationCurrency,
    ],
    [appConfig.env, confirmationCurrency, normalizedKey],
  );
  const shouldClearHardReloadCache = useMemo(isBrowserHardReload, []);
  const [hasClearedHardReloadCache, setHasClearedHardReloadCache] = useState(
    !shouldClearHardReloadCache,
  );

  useEffect(() => {
    if (!shouldClearHardReloadCache) {
      return;
    }

    queryClient.removeQueries({ queryKey, exact: true });
    setHasClearedHardReloadCache(true);
  }, [queryClient, queryKey, shouldClearHardReloadCache]);

  const query = useQuery<HotelBookingConfirmationResponse | null>({
    queryKey,
    queryFn: async (): Promise<HotelBookingConfirmationResponse | null> => {
      try {
        const token = await hotelTokenQuery.refetchTokenByEndpoint(
          HOTEL_CHECKOUT_TOKEN_ENDPOINT,
        );

        if (!token) {
          throw new Error('Hotel booking confirmation token is unavailable');
        }

        return await api.getBookingConfirmation(
          normalizedKey,
          token,
          clientId,
          confirmationCurrency,
        );
      } catch (error) {
        // TanStack treats `undefined` as a cancelled cache update. Store null
        // so an expired journey key cannot leave a stale confirmation visible.
        queryClient.setQueryData<HotelBookingConfirmationResponse | null>(
          queryKey,
          null,
        );
        throw error;
      }
    },
    enabled:
      !!normalizedKey &&
      !!clientId &&
      !!hotelConfig?.jwtHash &&
      !!hotelConfig?.xorKey &&
      !!appConfig.env &&
      hasClearedHardReloadCache,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  if (shouldClearHardReloadCache && !hasClearedHardReloadCache) {
    return {
      ...query,
      data: undefined,
      isLoading: true,
      isPending: true,
    };
  }

  return query;
};

export const useHotelBookingPrecancellation = (
  preCancelHref?: string,
  enabled = false,
) => {
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const hotelTokenQuery = useHotelToken();
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);

  return useQuery<HotelBookingPrecancellationResponse>({
    queryKey: hotelsKeys.bookingPrecancellation(preCancelHref || ''),
    queryFn: async () => {
      const token = await hotelTokenQuery.refetchToken();

      return api.getBookingPrecancellation(
        preCancelHref || '',
        token || '',
        clientId,
      );
    },
    enabled: enabled && Boolean(preCancelHref),
    retry: 0,
  });
};

export const useHotelCancelBooking = () => {
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const hotelTokenQuery = useHotelToken();
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);

  return useMutation<
    HotelCancelBookingResponse,
    unknown,
    HotelCancelBookingRequest
  >({
    mutationFn: async (request) => {
      const token = await hotelTokenQuery.refetchTokenByEndpoint(
        HOTEL_CHECKOUT_TOKEN_ENDPOINT,
      );

      return api.cancelBooking(request, token || '', clientId);
    },
    retry: 0,
  });
};

export const useHotelEmailVoucher = (
  request?: Partial<HotelEmailVoucherRequest>,
) => {
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const hotelTokenQuery = useHotelToken();
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);
  const email = request?.email?.trim() || '';
  const voucherHref = request?.voucherHref || '';

  return useQuery<HotelEmailVoucherResponse>({
    queryKey: hotelsKeys.bookingEmailVoucher(voucherHref, email),
    queryFn: async () => {
      const token = await hotelTokenQuery.refetchTokenByEndpoint(
        HOTEL_CHECKOUT_TOKEN_ENDPOINT,
      );

      return api.sendHotelVoucherEmail(
        {
          email,
          voucherHref,
        },
        token || '',
        clientId,
      );
    },
    enabled: false,
    retry: 0,
  });
};

export const useHotelBookingRoomContent = ({
  hotelId,
  roomId,
  language,
}: {
  hotelId?: string;
  roomId?: string;
  language?: string;
}) => {
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const normalizedHotelId = hotelId?.trim() || '';
  const normalizedRoomId = roomId?.trim() || '';
  const normalizedLanguage = normalizeBookingContentLanguage(language);
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);

  return useQuery({
    queryKey: [
      ...hotelsKeys.bookingRoomContent(
        normalizedHotelId,
        normalizedRoomId,
        normalizedLanguage,
      ),
      appConfig.env,
    ],
    queryFn: (): Promise<HotelBookingRoomContentResponse> =>
      api.getBookingRoomContent(
        normalizedHotelId,
        normalizedRoomId,
        normalizedLanguage,
        clientId,
      ),
    enabled:
      !!normalizedHotelId &&
      !!normalizedRoomId &&
      !!normalizedLanguage &&
      !!clientId &&
      !!appConfig.env,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
};

export const useHotelBookingHotelContent = ({
  hotelId,
  language,
}: {
  hotelId?: string;
  language?: string;
}) => {
  const { config: appConfig } = useAppConfig();
  const { config: hotelConfig } = useHotelConfig();
  const api = useHotelsApiService();
  const normalizedHotelId = hotelId?.trim() || '';
  const normalizedLanguage = normalizeBookingContentLanguage(language);
  const clientId = getHotelServiceAccountClientId(hotelConfig?.clientId);

  return useQuery({
    queryKey: [
      ...hotelsKeys.bookingHotelContent(normalizedHotelId, normalizedLanguage),
      appConfig.env,
    ],
    queryFn: (): Promise<HotelDetailResponse> =>
      api.getBookingHotelContent(
        normalizedHotelId,
        normalizedLanguage,
        clientId,
      ),
    enabled:
      !!normalizedHotelId &&
      !!normalizedLanguage &&
      !!clientId &&
      !!appConfig.env,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });
};

export const useRecentSearches = (onPressHandler?: (index: number) => void) => {
  const { logEvent } = useLogger();
  const { width: screenWidth } = useWindowDimensions();
  const { recentSearches } = useRecentSearchesStorage();
  const { config } = useAppConfig();

  return useQuery({
    queryKey: ['recentsearches', config.env],
    queryFn: async () => {
      // Use real recent searches data, limited to 3 most recent
      const limitedRecentSearches = recentSearches.slice(0, 3);
      return getRecentSearchContentCards(
        limitedRecentSearches,
        logEvent,
        screenWidth,
        onPressHandler,
        config.env,
      );
    },
    enabled: recentSearches.length > 0,
  });
};

export const useCachedHotelListData = (
  params?: HotelListParams,
  locationName: string = '',
) => {
  const queryClient = useQueryClient();
  const { currency } = useCurrency();
  const searchParamsFromUrl = useHotelSearchParams();

  // Find the MOST RECENT query key in the cache to inherit its missing params
  // This prevents MapView from triggering a duplicate fetch on initial load
  // due to missing defaults (like occupancy, asProfile) that HotelListView provides.
  const recentQueryKey = useMemo(() => {
    const matchingQueries = queryClient.getQueriesData<any>({
      queryKey: hotelsKeys.hotelsList,
    });
    if (!matchingQueries || matchingQueries.length === 0) return null;

    const locationId = params?.location_id || searchParamsFromUrl?.location_id;
    const reversed = [...matchingQueries].reverse();

    const matchedQuery =
      (locationId
        ? reversed.find(
            ([key, d]) =>
              Array.isArray(key) &&
              key.includes(locationId) &&
              d?.pages?.some((p: any) => p?.hotels?.length > 0),
          )
        : undefined) ??
      reversed.find(
        ([_, d]) =>
          d?.pages?.some((p: any) => p?.hotels?.length > 0) ||
          d?.hotels?.length > 0,
      );

    return matchedQuery?.[0] as unknown[] | null;
  }, [queryClient, params?.location_id, searchParamsFromUrl?.location_id]);

  const queryParams = useMemo(() => {
    const baseParams = params || searchParamsFromUrl;

    // Inherit from the exact query key shape:
    // [..., env, currency, location, checkin, checkout, occupancy, ...]
    const baseIndex = hotelsKeys.hotelsList.length + 1; // +1 for env
    const qOccupancy = recentQueryKey?.[baseIndex + 4] as string | undefined;
    const qAsProfile = recentQueryKey?.[baseIndex + 14] as string | undefined;

    return {
      ...baseParams,
      currency: resolveHotelSearchCurrency(
        [currency, baseParams?.currency],
        DEFAULT_CURRENCY,
      ),
      occupancy: baseParams?.occupancy || qOccupancy || '2',
      asProfile: (baseParams as any)?.asProfile ?? qAsProfile ?? '0',
    };
  }, [params, searchParamsFromUrl, currency, recentQueryKey]);

  const query = useHotelSearch(queryParams, locationName);

  return {
    ...query,
    data: query.data?.hotels || [],
    popularFilters: query.data?.popularFilters ?? [],
  };
};
